import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../style.css';

const API = 'https://culturlingua.com/blog_api.php';

/* Safari-safe hex darkening — replaces color-mix() */
function darkenHex(hex, amount = 0.35) {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h, 16);
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8)  & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round(( num        & 0xff) * (1 - amount)));
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function formatDate(str) {
  return new Date(str).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

/* Reading-progress bar */
function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el    = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? (el.scrollTop / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div style={{ position:'fixed', top:'72px', left:0, right:0, height:'3px',
      background:'rgba(255,255,255,.06)', zIndex:100, pointerEvents:'none' }}>
      <div style={{ height:'100%', width:`${pct}%`,
        background:'linear-gradient(90deg, var(--blue), var(--gold))',
        transition:'width .1s linear' }} />
    </div>
  );
}

/* Returns colors and pattern for the post hero — used for background only */
function HeroVisual({ color, category }) {
  const c1 = color || '#1a1744';
  const c2 = darkenHex(c1, 0.45);
  return { c1, c2 };
}

const S = `
@keyframes spin    { to { transform: rotate(360deg) } }
@keyframes fadeUp  { from { opacity:0; transform:translateY(18px) } to { opacity:1; transform:none } }
@keyframes shimmer { from { background-position:-200% 0 } to { background-position:200% 0 } }

/* ── Hero ── */
.bp-hero { padding:64px 48px 0; position:relative; overflow:hidden; }
.bp-hero-inner { max-width:760px; margin:0 auto; position:relative; z-index:2; animation:fadeUp .6s ease both; }
.bp-breadcrumb { display:flex; align-items:center; gap:10px; margin-bottom:32px; flex-wrap:wrap; }
.bp-back { color:rgba(255,255,255,.45); font-size:.85rem; text-decoration:none; transition:color .2s; display:inline-flex; align-items:center; gap:6px; }
.bp-back:hover { color:rgba(255,255,255,.8); }
.bp-sep { color:rgba(255,255,255,.16); }
.bp-cat-pill { font-size:.68rem; font-weight:800; text-transform:uppercase; letter-spacing:.08em; padding:4px 12px; border-radius:100px; }

.bp-title { font-family:'Fraunces',serif; font-size:clamp(1.8rem,4vw,3rem); font-weight:900; color:white; line-height:1.12; letter-spacing:-.5px; margin-bottom:20px; }
.bp-excerpt { color:rgba(255,255,255,.6); font-size:1.05rem; line-height:1.78; margin-bottom:32px; }
.bp-meta { display:flex; gap:8px; flex-wrap:wrap; align-items:center; padding-bottom:40px; }
.bp-meta-item { color:rgba(255,255,255,.38); font-size:.8rem; display:inline-flex; align-items:center; gap:4px; }
.bp-meta-dot { color:rgba(255,255,255,.14); font-size:.6rem; }

/* White wave connector from hero to body */
.bp-wave { display:block; width:100%; height:48px; position:relative; }

/* ── Body ── */
.bp-body { background:var(--cream); padding:0 48px 80px; }
.bp-body-inner { max-width:760px; margin:0 auto; padding-top:52px; }

/* ── Article content ── */
.blog-content { font-family:'DM Sans',sans-serif; color:#2d2d2d; line-height:1.9; font-size:1.06rem; }
.blog-content > *:first-child { margin-top:0; }
.blog-content h2 { font-family:'Fraunces',serif; font-size:1.65rem; font-weight:900; color:var(--navy); margin:56px 0 18px; line-height:1.2; letter-spacing:-.35px; }
.blog-content h2::before { content:''; display:block; width:36px; height:3px; background:var(--gold); border-radius:2px; margin-bottom:12px; }
.blog-content h3 { font-family:'Fraunces',serif; font-size:1.2rem; font-weight:800; color:var(--navy); margin:36px 0 12px; }
.blog-content p { margin-bottom:24px; }
.blog-content ul,.blog-content ol { padding-left:20px; margin-bottom:24px; }
.blog-content li { margin-bottom:10px; padding-left:4px; }
.blog-content ul li::marker { color:var(--gold); }
.blog-content ol li::marker { color:var(--blue); font-weight:700; }
.blog-content strong { color:var(--navy); font-weight:700; }
.blog-content em { font-style:italic; color:#555; }
.blog-content a { color:var(--blue); font-weight:600; text-decoration:none; border-bottom:1.5px solid rgba(59,111,224,.25); transition:border-color .2s,color .2s; }
.blog-content a:hover { border-bottom-color:var(--blue); }
.blog-content blockquote {
  border-left:none;
  padding:28px 32px;
  margin:36px 0;
  background:linear-gradient(135deg,rgba(245,200,66,.07) 0%,rgba(245,200,66,.03) 100%);
  border-radius:16px;
  font-style:italic;
  color:#555;
  font-size:1.08rem;
  position:relative;
  border:1px solid rgba(245,200,66,.18);
}
.blog-content blockquote::before {
  content:'"';
  position:absolute; top:-4px; left:20px;
  font-family:'Fraunces',serif; font-size:4rem; font-weight:900;
  color:var(--gold); line-height:1; opacity:.6;
}
.blog-content img { max-width:100%; border-radius:14px; margin:8px 0; display:block; }
.blog-content hr { border:none; height:1px; background:linear-gradient(90deg,transparent,#e0dbd0,transparent); margin:48px 0; }
.blog-content code { background:rgba(26,23,68,.06); color:var(--navy); padding:2px 8px; border-radius:6px; font-size:.88em; font-family:'Courier New',monospace; }
.blog-content pre { background:var(--navy); color:#c8d8ff; padding:28px; border-radius:14px; overflow-x:auto; margin:28px 0; }
.blog-content pre code { background:transparent; color:inherit; padding:0; }
.blog-content table { width:100%; border-collapse:collapse; margin:28px 0; border-radius:12px; overflow:hidden; }
.blog-content th { background:var(--navy); color:white; padding:12px 16px; text-align:left; font-size:.85rem; font-weight:700; }
.blog-content td { padding:11px 16px; border-bottom:1px solid #ede9e0; font-size:.93rem; }
.blog-content tr:last-child td { border-bottom:none; }
.blog-content tr:nth-child(even) td { background:rgba(245,200,66,.04); }

/* ── Share strip ── */
.bp-share { display:flex; align-items:center; gap:12px; padding:20px 24px; background:white; border-radius:14px; margin-bottom:36px; box-shadow:0 2px 12px rgba(26,23,68,.06); }
.bp-share-lbl { font-size:.75rem; font-weight:800; text-transform:uppercase; letter-spacing:.1em; color:#bbb; margin-right:4px; }
.bp-share-btn { padding:7px 16px; border-radius:100px; border:1.5px solid #e8e2d8; background:white; color:#555; font-family:'DM Sans',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .2s; text-decoration:none; display:inline-flex; align-items:center; gap:6px; }
.bp-share-btn:hover { border-color:var(--navy); color:var(--navy); transform:translateY(-1px); }

/* ── CTA box ── */
.bp-cta { border-radius:24px; margin-top:64px; overflow:hidden; position:relative; }
.bp-cta-top { background:var(--navy); padding:44px 44px 36px; text-align:center; position:relative; overflow:hidden; }
.bp-cta-orb { position:absolute; top:-80px; right:-80px; width:280px; height:280px; background:radial-gradient(circle,rgba(245,200,66,.13) 0%,transparent 70%); pointer-events:none; }
.bp-cta-orb2 { position:absolute; bottom:-60px; left:-60px; width:200px; height:200px; background:radial-gradient(circle,rgba(59,111,224,.13) 0%,transparent 70%); pointer-events:none; }
.bp-cta-badge { display:inline-flex; align-items:center; gap:6px; background:rgba(245,200,66,.12); border:1px solid rgba(245,200,66,.28); border-radius:100px; padding:5px 14px; font-size:.72rem; font-weight:800; letter-spacing:.07em; text-transform:uppercase; color:var(--gold); margin-bottom:18px; }
.bp-cta h3 { font-family:'Fraunces',serif; color:white; font-size:1.55rem; font-weight:900; margin-bottom:10px; letter-spacing:-.3px; line-height:1.2; }
.bp-cta-sub { color:rgba(255,255,255,.55); margin-bottom:0; line-height:1.65; font-size:.97rem; max-width:380px; margin-left:auto; margin-right:auto; }
.bp-cta-bottom { background:#f5f0e8; padding:20px 44px 28px; }
.bp-cta-perks { display:flex; justify-content:center; gap:6px; flex-wrap:wrap; margin-bottom:24px; margin-top:0; }
.bp-cta-perk { display:inline-flex; align-items:center; gap:5px; font-size:.78rem; font-weight:600; color:#555; background:white; padding:5px 12px; border-radius:100px; border:1px solid #e5dfd5; white-space:nowrap; }
.bp-cta-perk svg { flex-shrink:0; }
.bp-cta-btns { display:flex; gap:10px; justify-content:center; flex-wrap:wrap; }
.bp-cta-social { margin-top:16px; text-align:center; }
.bp-cta-social-line { font-size:.75rem; color:#aaa; display:flex; align-items:center; justify-content:center; gap:8px; }
.bp-cta-social-line::before,.bp-cta-social-line::after { content:''; flex:1; max-width:60px; height:1px; background:#ddd; }
.bp-cta-avatars { display:inline-flex; margin-right:6px; }
.bp-cta-avatar { width:22px; height:22px; border-radius:50%; border:2px solid #f5f0e8; background:var(--navy); color:white; font-size:.55rem; font-weight:800; display:flex; align-items:center; justify-content:center; margin-left:-6px; }
.bp-cta-avatar:first-child { margin-left:0; }

/* ── Related ── */
.bp-related { margin-top:56px; }
.bp-rel-lbl { font-size:.68rem; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:#c0b8a8; margin-bottom:22px; display:flex; align-items:center; gap:12px; }
.bp-rel-lbl::after { content:''; flex:1; height:1px; background:#e8e2d8; }
.bp-rel-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }
.bp-rel-card { text-decoration:none; background:white; border-radius:16px; overflow:hidden; box-shadow:0 2px 12px rgba(26,23,68,.06); transition:transform .22s ease,box-shadow .22s ease; display:flex; flex-direction:column; }
.bp-rel-card:hover { transform:translateY(-4px); box-shadow:0 12px 36px rgba(26,23,68,.12); }
.bp-rel-visual { height:88px; position:relative; overflow:hidden; background:white; }
.bp-rel-body { padding:18px 20px 16px; flex:1; }
.bp-rel-title { font-family:'Fraunces',serif; color:var(--navy); font-weight:800; line-height:1.3; margin-bottom:10px; font-size:.98rem; }
.bp-rel-cta { color:var(--blue); font-size:.82rem; font-weight:700; display:inline-flex; align-items:center; gap:4px; transition:gap .18s; }
.bp-rel-card:hover .bp-rel-cta { gap:8px; }

/* ── Fullscreen states ── */
.bp-fullscreen { min-height:60vh; display:flex; align-items:center; justify-content:center; background:var(--cream); }

@media (max-width:860px) {
  .bp-hero,.bp-body { padding-left:20px; padding-right:20px; }
  .bp-cta-top { padding:36px 24px 28px; }
  .bp-cta-bottom { padding:18px 24px 24px; }
}
@media (max-width:560px) {
  .bp-rel-grid { grid-template-columns:1fr; }
  .bp-share { flex-wrap:wrap; }
}
`;

function RelVisual({ color, icon, category }) {
  const c1 = color || '#3b6fe0';
  const darken = darkenHex(c1, 0.28);
  const initials = (category || 'Ge').substring(0, 2).toUpperCase();
  return (
    <div className="bp-rel-visual" style={{background:'white', position:'relative'}}>
      {/* Accent strip */}
      <div style={{position:'absolute',top:0,left:0,right:0,height:'4px',
        background:`linear-gradient(90deg,${c1},${darken})`}}/>
      {/* Emoji pill */}
      <div style={{
        display:'flex', alignItems:'center', gap:'10px',
        padding:'16px 18px 0', position:'relative', zIndex:2,
      }}>
        <div style={{
          width:'40px', height:'40px', borderRadius:'10px',
          background:`${c1}14`, display:'flex', alignItems:'center',
          justifyContent:'center', fontSize:'20px', lineHeight:1, flexShrink:0,
        }}>
          {icon || <span style={{fontFamily:"'Fraunces',serif",fontSize:'1rem',fontWeight:900,color:c1}}>{initials}</span>}
        </div>
        <span style={{fontSize:'10px',fontWeight:800,letterSpacing:'.07em',
          textTransform:'uppercase',color:c1}}>{category}</span>
      </div>
    </div>
  );
}

/* ── Tracking helpers (module-level, not recreated on render) ── */
function getSessionId() {
  let sid = sessionStorage.getItem('cl_sid');
  if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('cl_sid', sid); }
  return sid;
}
function getDevice() {
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  return 'desktop';
}
function getUTMs() {
  const p = new URLSearchParams(window.location.search);
  return { utm_source: p.get('utm_source')||'', utm_medium: p.get('utm_medium')||'', utm_campaign: p.get('utm_campaign')||'' };
}

export default function BlogPost() {
  const { slug }                = useParams();
  const [post, setPost]         = useState(null);
  const [related, setRelated]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  const scrollMaxRef   = React.useRef(0);
  const timeActiveRef  = React.useRef(0);
  const ctaClicksRef   = React.useRef(0);
  const sessionIdRef   = React.useRef(getSessionId());
  const engageSentRef  = React.useRef(false);

  /* ── 1. Fetch post + fire view event ── */
  useEffect(() => {
    setLoading(true); setNotFound(false); setPost(null); setRelated([]);
    scrollMaxRef.current = 0; timeActiveRef.current = 0;
    ctaClicksRef.current = 0; engageSentRef.current = false;

    fetch(`${API}?action=post&slug=${slug}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(data => {
        setPost(data); setLoading(false);
        const ref = document.referrer;
        const isInternal = ref && (ref.includes('culturlingua.com') || ref.includes('localhost'));
        fetch(`${API}?action=view&slug=${slug}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: isInternal ? 'internal' : 'organic', device: getDevice(), session_id: sessionIdRef.current, ...getUTMs() }),
        });
        return fetch(`${API}?action=posts&category=${encodeURIComponent(data.category)}`);
      })
      .then(r => r.json())
      .then(all => setRelated((Array.isArray(all) ? all : []).filter(p => p.slug !== slug).slice(0, 2)))
      .catch(() => { setLoading(false); setNotFound(true); });
  }, [slug]);

  /* ── 2. Scroll depth ── */
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const pct = el.scrollHeight - el.clientHeight > 0 ? Math.round((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100) : 0;
      if (pct > scrollMaxRef.current) scrollMaxRef.current = pct;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [slug]);

  /* ── 3. Active time (pauses when tab hidden) ── */
  useEffect(() => {
    if (!post) return;
    let interval = null;
    const start = () => { if (!interval) interval = setInterval(() => { timeActiveRef.current += 1; }, 1000); };
    const stop  = () => { clearInterval(interval); interval = null; };
    const onVis = () => (document.hidden ? stop() : start());
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, [post, slug]);

  /* ── 4. Flush on leave / slug change ── */
  const flushEngage = React.useCallback((targetSlug) => {
    if (engageSentRef.current) return;
    engageSentRef.current = true;
    const bounced = scrollMaxRef.current < 10 && timeActiveRef.current < 10;
    const payload = JSON.stringify({ session_id: sessionIdRef.current, scroll_max: scrollMaxRef.current, time_active: timeActiveRef.current, cta_clicks: ctaClicksRef.current, bounced });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}?action=engage&slug=${targetSlug}`, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(`${API}?action=engage&slug=${targetSlug}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true });
    }
  }, []);

  useEffect(() => {
    if (!post) return;
    const captured = slug;
    const onUnload = () => flushEngage(captured);
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);
    return () => { flushEngage(captured); window.removeEventListener('pagehide', onUnload); window.removeEventListener('beforeunload', onUnload); };
  }, [post, slug, flushEngage]);

  if (loading) return (
    <div className="bp-fullscreen">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{textAlign:'center'}}>
        <div style={{display:'inline-block',width:'36px',height:'36px',
          border:'3px solid #e5e7eb',borderTopColor:'var(--blue)',
          borderRadius:'50%',animation:'spin .8s linear infinite'}}/>
      </div>
    </div>
  );

  if (notFound || (!post && !loading)) return (
    <div className="bp-fullscreen" style={{flexDirection:'column',gap:'16px',textAlign:'center',padding:'48px'}}>
      <div style={{fontSize:'3rem'}}>📭</div>
      <h2 style={{fontFamily:"'Fraunces',serif",color:'var(--navy)'}}>Artículo no encontrado</h2>
      <p style={{color:'#888'}}>Este artículo no existe o fue eliminado.</p>
      <Link to="/blog" style={{color:'var(--blue)',fontWeight:700}}>← Ver todos los artículos</Link>
    </div>
  );

  const { c1, c2 } = HeroVisual({
    color: post.category_color,
    category: post.category,
  });

  const shareUrl = `https://culturlingua.com/blog/${post.slug}`;

  return (
    <div className="page-wrapper">
      <style>{S}</style>
      <ReadingProgress />

      <Helmet key={post.slug}>
        <title>{post.title} | Culturlingua</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:author" content="Culturlingua" />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"BlogPosting",
          "mainEntityOfPage":{"@type":"WebPage","@id":shareUrl},
          "headline":post.title,"description":post.excerpt,"url":shareUrl,
          "datePublished":post.created_at,"dateModified":post.created_at,
          "author":{"@type":"Organization","name":"Culturlingua"},
          "publisher":{"@type":"Organization","name":"Culturlingua",
            "logo":{"@type":"ImageObject","url":"https://culturlingua.com/logo.png"}}
        })}</script>
      </Helmet>

      {/* ── Hero (colored header) ── */}
      <div className="bp-hero" style={{ background:'var(--navy)' }}>
        {/* Subtle dot grid — same as rest of site */}
        <div style={{position:'absolute',inset:0,
          backgroundImage:'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize:'28px 28px', pointerEvents:'none'}}/>
        {/* Single soft orb top-right */}
        <div style={{position:'absolute',top:'-60px',right:'-60px',width:'280px',height:'280px',
          background:'radial-gradient(circle,rgba(245,200,66,.09) 0%,transparent 70%)',pointerEvents:'none'}}/>
        {/* Emoji — clean, no blur, top-right */}
        {post.icon && (
          <div style={{position:'absolute',top:'36px',right:'52px',fontSize:'64px',
            opacity:0.18,userSelect:'none',pointerEvents:'none',lineHeight:1,
          }}>{post.icon}</div>
        )}

        <div className="bp-hero-inner">
          {/* Breadcrumb */}
          <div className="bp-breadcrumb">
            <Link to="/blog" className="bp-back">← Blog</Link>
            <span className="bp-sep">/</span>
            <span className="bp-cat-pill" style={{
              background:`${c1}28`, color:c1,
              border:`1px solid ${c1}44`,
            }}>{post.category}</span>
          </div>

          <h1 className="bp-title">{post.title}</h1>
          <p className="bp-excerpt">{post.excerpt}</p>

          <div className="bp-meta">
            <span className="bp-meta-item">📅 {formatDate(post.created_at)}</span>
            <span className="bp-meta-dot">●</span>
            <span className="bp-meta-item">⏱ {post.read_time}</span>
            {post.views > 100 && (
  <span className="bp-meta-item">
    👁️ {post.views.toLocaleString()} lecturas
  </span>
)}
            <span className="bp-meta-dot">●</span>
            <span className="bp-meta-item">✍️ Culturlingua</span>
          </div>
        </div>

        {/* Category color accent strip at bottom of hero */}
        <div style={{
          position:'absolute', bottom:0, left:0, right:0, height:'4px',
          background:`linear-gradient(90deg, ${c1}, ${c2})`,
        }}/>
        {/* Wave transition */}
        <svg className="bp-wave" viewBox="0 0 1440 48" preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M0,48 C360,0 1080,48 1440,16 L1440,48 Z" fill="var(--cream)"/>
        </svg>
      </div>

      {/* ── Article body ── */}
      <div className="bp-body">
        <div className="bp-body-inner">

          {/* Share strip */}
          <div className="bp-share">
            <span className="bp-share-lbl">Compartir</span>
            <a className="bp-share-btn" target="_blank" rel="noreferrer"
              href={`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + shareUrl)}`}>
              💬 WhatsApp
            </a>
            <a className="bp-share-btn" target="_blank" rel="noreferrer"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}>
              🔗 LinkedIn
            </a>
            <a className="bp-share-btn" target="_blank" rel="noreferrer"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`}>
              𝕏 Twitter
            </a>
          </div>

          {/* Content */}
          <article className="blog-content"
            dangerouslySetInnerHTML={{ __html: post.content }} />

          {/* CTA */}
          <div className="bp-cta">
            {/* Top — navy dark section */}
            <div className="bp-cta-top">
              <div className="bp-cta-orb"/><div className="bp-cta-orb2"/>
              <div style={{position:'relative',zIndex:2}}>
                <div className="bp-cta-badge">
                  <span>✦</span> Prueba gratuita
                </div>
                <h3>Descubre tu nivel de inglés<br/>en 10 minutos — gratis</h3>
                <p className="bp-cta-sub">
                  Sin registro, sin tarjeta. Solo un examen rápido y recibes
                  una recomendación personalizada de curso.
                </p>
              </div>
            </div>
            {/* Bottom — cream section with perks + buttons */}
            <div className="bp-cta-bottom">
              <div className="bp-cta-perks">
                <span className="bp-cta-perk">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6.5" fill="#22c55e" opacity=".15"/><path d="M4 6.5l1.8 1.8L9 4.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Grupos pequeños (max 8)
                </span>
                <span className="bp-cta-perk">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6.5" fill="#22c55e" opacity=".15"/><path d="M4 6.5l1.8 1.8L9 4.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Maestros certificados
                </span>
                <span className="bp-cta-perk">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6.5" fill="#22c55e" opacity=".15"/><path d="M4 6.5l1.8 1.8L9 4.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Tlaquepaque, Jalisco
                </span>
              </div>
              <div className="bp-cta-btns">
                <Link to="/Examen-de-ubicacion?from=blog" className="btn-glow"
                  onClick={() => { ctaClicksRef.current += 1; }}>
                  Hacer mi examen gratis →
                </Link>
                <a className="btn-outline"
                  href={`https://wa.me/523314567890?text=${encodeURIComponent('Hola, leí un artículo del blog y me gustaría información sobre los cursos de inglés.')}`}
                  target="_blank" rel="noreferrer"
                  onClick={() => { ctaClicksRef.current += 1; }}>
                  💬 Hablar con un asesor
                </a>
              </div>
              <div className="bp-cta-social">
                <div className="bp-cta-social-line">
                  <span className="bp-cta-avatars">
                    {['MR','LG','AP','KF'].map((i,idx) => (
                      <span key={idx} className="bp-cta-avatar"
                        style={{background:['#1a1744','#3b6fe0','#f5c842','#22c55e'][idx],
                          color:idx===2?'#1a1744':'white'}}>
                        {i}
                      </span>
                    ))}
                  </span>
                  Más de 500 alumnos han avanzado con Culturlingua
                </div>
              </div>
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="bp-related">
              <div className="bp-rel-lbl">Artículos relacionados</div>
              <div className="bp-rel-grid">
                {related.map(r => (
                  <Link key={r.slug} to={'/blog/' + r.slug} className="bp-rel-card">
                    <RelVisual color={r.category_color} icon={r.icon} category={r.category}/>
                    <div className="bp-rel-body">
                      <div className="bp-rel-title">{r.title}</div>
                      <span className="bp-rel-cta">Leer <span>→</span></span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}